/**
 * Represents the login page component.
 * @param {Object} props - The component props.
 * @param {Function} props.setToken - The function to set the authentication token.
 * @returns {JSX.Element} The rendered login page.
 */
import React, { useReducer, useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CardHeader from '@mui/material/CardHeader';
import Styles from './Login.module.css';
import PropTypes from 'prop-types';
import * as CryptoJS from 'crypto-js';
import { useNavigate } from 'react-router-dom';
import styles from '../layout/Layout.module.css';
import { Box, Button, Link } from '@mui/material';
import brandlogoLight from '../../components/logo/VIRA LOGO - LIGHT MODE (002).png';
import brandlogoDark from '../../components/logo/VIRA LOGO - DARK MODE (002).png';
import { publicClientApp } from '../../config/msal-config';
import CircularProgress from '@mui/material/CircularProgress';
import { FlagPrideIntersexInclusiveProgress16Filled } from '@fluentui/react-icons';
import { useTheme } from '@mui/material/styles';

type State = {
  username: string;
  password: string;
  isButtonDisabled: boolean;
  helperText: string;
  isError: boolean;
  loading: boolean;
};

const initialState: State = {
  username: '',
  password: '',
  isButtonDisabled: true,
  helperText: '',
  isError: false,
  loading: false,
};

type Action =
  | { type: 'setUsername'; payload: string }
  | { type: 'setPassword'; payload: string }
  | { type: 'setIsButtonDisabled'; payload: boolean }
  | { type: 'loginSuccess'; payload: string }
  | { type: 'loginFailed'; payload: string }
  | { type: 'setIsError'; payload: boolean }
  | { type: 'setLoading'; payload: boolean };

/**
 * Reducer function for managing the state of the login component.
 *
 * @param state - The current state of the component.
 * @param action - The action object that describes the state change.
 * @returns The new state after applying the action.
 *
 *
 */

interface MyTokenClaims {
  name: string;
  email: string;
  exp: number;
  // add other claims as needed
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'setUsername':
      return {
        ...state,
        username: action.payload,
      };
    case 'setPassword':
      return {
        ...state,
        password: action.payload,
      };
    case 'setIsButtonDisabled':
      return {
        ...state,
        isButtonDisabled: action.payload,
      };
    case 'loginSuccess':
      return {
        ...state,
        helperText: action.payload,
        isError: false,
      };
    case 'loginFailed':
      return {
        ...state,
        helperText: action.payload,
        isError: true,
      };
    case 'setIsError':
      return {
        ...state,
        isError: action.payload,
      };
    case 'setLoading':
      return {
        ...state,
        loading: action.payload,
      };
  }
};

/**
 * Represents the login component.
 * @param {Function} setToken - A function to set the authentication token.
 * @returns {JSX.Element} The login component.
 */
//@ts-ignore
const Login = ({ setToken }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [username, setUserNameState] = useState('');
  const [password, setPasswordState] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  // const classes = useStyles();
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (state.username.trim() && state.password.trim()) {
      dispatch({
        type: 'setIsButtonDisabled',
        payload: false,
      });
    } else {
      dispatch({
        type: 'setIsButtonDisabled',
        payload: true,
      });
    }
  }, [state.username, state.password]);

  /**
   * Logs in a user with the provided username and password.
   * @param {string} username - The username of the user.
   * @param {string} password - The password of the user.
   * @returns {Promise<object>} - A promise that resolves to an object containing the login status and data.
   */
  //@ts-ignore

  async function loginUser(username, password) {
    const hashedPassword = CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
    const api_key = process.env.REACT_APP_LOGIN_API_KEY || '';
    const response = await fetch(
      process.env.REACT_APP_LOGIN_URL ||
        'https://funcapp-westus-utils-kai-pmr-f8g2fdb6fsfkaqh7.westus-01.azurewebsites.net/api/login_api?code=FWmZV1Dt01KUL3BoP6lI0wvx_OFZEq_CuCUvhnNNtRmeAzFuyefTDQ==',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': api_key,
        },
        body: JSON.stringify({ email: username, password: hashedPassword }),
      }
    );

    if (response.status === 200) {
      const data = await response.json();

      data['status'] = 200;
      return data;
    }

    // return {
    //   "id": 1,
    //   "name": "Twitch User",
    //   "email": "twitch@tavant.com",
    //   "status": 200,
    // };

    return { status: response.status };
  }

  //@ts.ignore
  /**
   * Handles the login functionality.
   * If the username and password are empty, dispatches a loginFailed action with an error message.
   * Otherwise, calls the loginUser function and handles the response accordingly.
   * If the response is undefined or has a status other than 200, dispatches a loginFailed action with an error message.
   * If the response is successful, navigates to the home page, sets the token, and dispatches a loginSuccess action.
   *
   *
   */

  const checkAndCreateUser = async (email: string, username: string, isAdmin: boolean): Promise<void> => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY || '';

      // Call the "list users" API to check if the user exists
      const listUsersResponse = await fetch(process.env.REACT_APP_LIST_USERS_URL || '', {
        method: 'GET',
        // headers: {
        //   'x-api-key': apiKey,
        // },
      });

      if (listUsersResponse.status === 200) {
        // console.log("Display First Name: ", username.split);
        const usersData = await listUsersResponse.json();
        // const userExists = Array.isArray(usersData) && usersData.some((user: { email: string }) => user.email === email);
        let userExists = false;
        let detailsWrong = false;
        const role = isAdmin ? 'admin' : 'user';
        const selectedUser = usersData.find((user: { email: string }) => user.email === email);

        usersData.forEach(
          (user: { email: string; name: string; role: string; department: string[]; proj_list: string[] }) => {
            if (user.email === email) {
              userExists = true;

              if (user.name !== username || user.role !== role) {
                detailsWrong = true;
              }
              return;
            }
          }
        );
        console.log('User exists:', userExists);
        console.log('Details wrong:', detailsWrong);

        if (!userExists || detailsWrong) {
          const department = selectedUser ? selectedUser.department : 'PRMI Org';

          const createUserResponse = await fetch(process.env.REACT_APP_CREATE_USER || '', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              // 'x-api-key': apiKey,
            },
            body: JSON.stringify({
              firstname: username.split(' ')[0],
              lastname: username.split(' ')[1],
              email: email,
              department: department,
              // password: hashedPassword,
              proj_list: ['investor-index', 'pmr-guidelines-index', 'General-chat'],
              role: role,
              update: detailsWrong,
            }),
          });
          if (createUserResponse.status === 200) {
            console.log('User created successfully.');
          } else {
            console.error('Failed to create user.');
          }
        } else {
          console.log('User already exists.');
        }
      } else {
        console.error('Failed to fetch users list.');
      }
    } catch (error) {
      console.error('Error checking or creating user:', error);
    }
  };
  const handleLogin = async () => {
    dispatch({
      type: 'setLoading',
      payload: true,
    });

    if (state.username === '' && state.password === '') {
      dispatch({
        type: 'loginFailed',
        payload: 'Empty username or password',
      });
    } else {
      const response = await loginUser(username, password);
      if (response === undefined || response.status !== 200) {
        dispatch({
          type: 'loginFailed',
          payload: 'Incorrect username or password',
        });
      } else {
        // window.location.reload();
        navigate('/', { replace: true });
        setToken({ ...response, isAdmin: true });
        dispatch({
          type: 'loginSuccess',
          payload: 'Login Successfully',
        });
      }
      dispatch({
        type: 'setLoading',
        payload: false,
      });
    }
  };

  const handleSSOLogin = async () => {
    setSsoLoading(true);
    try {
      // Perform login using MSAL
      const loginResponse = await publicClientApp.loginPopup({
        scopes: ['User.Read', 'GroupMember.Read.All'], // Replace with your required scopes
      });

      // Acquire token after login
      let tokenResponse;
      try {
        tokenResponse = await publicClientApp.acquireTokenSilent({
          account: loginResponse.account,
          scopes: ['User.Read', 'GroupMember.Read.All'],
        });
      } catch (silentError) {
        // Fallback to interactive if silent fails
        tokenResponse = await publicClientApp.acquireTokenPopup({
          scopes: ['User.Read', 'GroupMember.Read.All'],
        });
      }

      // Use the access token to fetch user details from Microsoft Graph API
      const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      });

      // console.log(tokenResponse.accessToken);

      if (graphResponse.ok) {
        const userData = await graphResponse.json();
        // console.log("User details fetched from Graph API:", userData);

        const groupResponse = await fetch('https://graph.microsoft.com/v1.0/me/memberOf', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        });
        let groupToken = null;
        let isAdmin = false;
        let isUser = false;
        if (groupResponse.ok) {
          const groupData = await groupResponse.json();
          // console.log("Group details fetched from Graph API:", groupData);
          const adminGroupId = process.env.REACT_APP_ADMIN_GRP_TENANT; // Replace with the actual admin group ID
          const userGrpId = process.env.REACT_APP_USER_GRP_TENANT;
          const adminUserPrmi = process.env.REACT_APP_ADMIN_GRP;
          const userGrpPrmi = process.env.REACT_APP_USER_GRP || '';
          isUser =
            groupData.value.some(
              (group: any) =>
                group.id === userGrpId ||
                group.id === adminGroupId ||
                group.id === adminUserPrmi ||
                group.id === userGrpPrmi
            ) || false;
          if (!isUser) {
            setSsoLoading(false);
            dispatch({
              type: 'loginFailed',
              payload: 'You are not authorized to login with this account.',
            });
            return; // Stop further execution
          }
          isAdmin =
            groupData.value.some((group: any) => group.id === adminGroupId || group.id === adminUserPrmi) || false;

          // Extract group token or group claim logic
          await checkAndCreateUser(userData.mail || userData.userPrincipalName, userData.displayName, isAdmin);
          groupToken = groupData.value.map((group: any) => group.id).join(',');
        } else {
          console.error('Failed to fetch group details:', groupResponse.statusText);
        }

        console.log(isAdmin);

        // Set the token and user details
        setToken({
          id: userData.id,
          name: userData.displayName,
          email: userData.mail || userData.userPrincipalName, // Use mail or userPrincipalName
          isAdmin: isAdmin,
        });

        // Navigate to the home page
        navigate('/', { replace: true });
      } else {
        console.error('Failed to fetch user details:', graphResponse.statusText);
      }
    } catch (error) {
      console.error('SSO Login failed:', error);
    } finally {
      setSsoLoading(false);
    }
  };

  /**
   * Handles the key press event.
   * If the Enter key is pressed, it checks if the login button is disabled or not.
   * If the button is not disabled, it calls the handleLogin function.
   *
   * @param event - The keyboard event object.
   */
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.keyCode === 13 || event.which === 13) {
      state.isButtonDisabled || handleLogin();
    }
  };

  /**
   * Handles the change event for the username input field.
   * Updates the username state and dispatches an action to update the username in the store.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event object.
   */
  const handleUsernameChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setUserNameState(event.target.value);
    dispatch({
      type: 'setUsername',
      payload: event.target.value,
    });
  };

  /**
   * Handles the change event for the password input field.
   * Updates the password state and dispatches an action to update the password in the store.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event object.
   */
  const handlePasswordChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setPasswordState(event.target.value);
    dispatch({
      type: 'setPassword',
      payload: event.target.value,
    });
  };
  return (
    <div className={styles.layout}>
      {/* <header className={styles.header} role={"banner"}>
        <Link className={styles.headerTitleContainer}>
          <h3 className={styles.headerTitle}>Knowledge AI</h3>
        </Link>
      </header> */}
      <div className={styles.headerContainer}></div>
      <form className={Styles.container} noValidate autoComplete="off">
        <Card className={Styles.card}>
          <div className={Styles.logoContainer}>
            <img
              src={theme.palette.mode === 'dark' ? brandlogoDark : brandlogoLight}
              alt="VIRA Logo"
              className={Styles.logoIcon}
            />
            <h1 className={Styles.brandTitle}></h1>
          </div>
          <div className={Styles.header}></div>

          <CardContent sx={{ width: '100%', paddingBottom: '6px' }}>
            <div>
              <TextField
                error={state.isError}
                fullWidth
                id="username"
                type="email"
                label="Username"
                placeholder="Username"
                margin="normal"
                onChange={handleUsernameChange}
                onKeyDown={handleKeyPress}
                className={Styles.inputField}
                size="small"
              />
              <TextField
                error={state.isError}
                fullWidth
                id="password"
                type="password"
                label="Password"
                placeholder="Password"
                margin="normal"
                helperText={state.helperText}
                onChange={handlePasswordChange}
                onKeyDown={handleKeyPress}
                className={Styles.inputField}
                size="small"
              />
            </div>
          </CardContent>
          <CardActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              padding: '0 16px 16px',
            }}
          >
            <Button
              variant="contained"
              size="large"
              className={Styles.loginBtn}
              onClick={handleLogin}
              disabled={state.isButtonDisabled}
            >
              {state.loading ? 'Logging in...' : 'LOGIN'}
            </Button>
            <Box className={Styles.divider}>OR</Box>
            <Button
              variant="contained"
              size="large"
              className={Styles.ssoBtn}
              onClick={handleSSOLogin}
              disabled={ssoLoading || state.loading}
              startIcon={ssoLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              LOGIN USING SSO
            </Button>
          </CardActions>
        </Card>
      </form>
    </div>
  );
};
Login.propTypes = {
  setToken: PropTypes.func.isRequired,
};
export default Login;
